import React, { useEffect, useState } from 'react'
import OrderSummaryRow from './OrderSummaryRow'
import { formatAmount } from '../utils/formatAmount';
import { useSelector } from 'react-redux';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import PaymentTextArea from './PaymentTextArea';
import { getUnpaidExtraJpFeesThb } from '../utils/extraJpFees';
import useBankTransferOnly from '../hooks/useBankTransferOnly';
import { COMPANY_BANK_NOTICE } from '../utils/companyBank';

const OrderSummaryTable = ({smileName, orders, orderSummary, groups, createSecondOrderPaymentLink, isAdmin = false, onOpenOrderDetail, shopNameOverride, formatAmountForTotal: formatAmountForTotalProp}) => {
  const style = 'px-1 py-2 md:px-3 md:py-3.5 text-center text-xxs font-semibold text-gray-900 md:text-sm border-r border-gray-300 3xl:text-xl';
  const groupHeaderStyle = 'px-2 py-2 md:px-3 md:py-2 text-left text-xs font-semibold text-gray-800 bg-amber-100 border-r border-gray-300';

  const [totalSum, setTotalSum] = useState(0)
  const shopNameFromRedux = useSelector((state) => state.shop.name);
  const shopName = shopNameOverride != null && shopNameOverride !== '' ? shopNameOverride : shopNameFromRedux;
  const { authState } = useAuth();

  // 2-decimal totals (was formatAmountRound = floor to whole baht, which hid satang
  // and made the rows not sum to the total once fees with satang joined)
  const fmtTotal = formatAmountForTotalProp ?? formatAmount;

  const calTotalSum = (orders) => {
    let sum = 0;
    orders.forEach(order => {
      // Add this check to exclude cancelled orders
      if (order.shipStatus === 'Cancel by store') {
        return; // Skip this order
      }

      const productOnly = order.showOnlyProductAmount === true;
      
      if (order.productPayment === 'รอชำระ' || order.productPayment === 'มัดจำ 50%' || order.productPayment === 'มัดจำ 30%') {
        let multiplier = 1; // Default full payment
        if (order.productPayment === 'มัดจำ 50%') multiplier = 0.5;
        if (order.productPayment === 'มัดจำ 30%') multiplier = 0.7;
        sum += order.amount * order.productRate * multiplier * order.qty;
        if(multiplier === 1 && shopName === 'tama')
          sum += order.transactionFee;
      }

      // Skip ship/weight/delivery for future orders (product amount only)
      if (productOnly) return;
      
      // Check and sum delivery fee in Japan if not fully paid
      if (order.deliveryFeeJp !== null && (order.shipJpPaid === 'รอชำระ' || order.shipJpPaid === 'มัดจำ 50%' || order.shipJpPaid === 'มัดจำ 30%')) {
        const rate = order.isCod ? order.codRate : order.productRate; // Use codRate if isCod is true, else use productRate
        let multiplier = 1; // Default full payment
        if (order.shipJpPaid === 'มัดจำ 50%') multiplier = 0.5;
        if (order.shipJpPaid === 'มัดจำ 30%') multiplier = 0.7;
        sum += order.deliveryFeeJp * rate * multiplier;
      }

      // Sum weight price if it exists, regardless of weight value
      if (order.weightPrice && !order.weightPaid) {
        sum += order.weightPrice;
      }
      // Oversize awaiting dimension measurement: price is set manually later —
      // never add a weight * weightRate estimate to the total
      else if (order.isOversize && !order.weightPaid) {
        // skip until admin sets weightPrice
      }
      // If no weightPrice but weight exists and not paid, calculate based on weight
      else if (order.weight !== null && order.weight !== 0 && !order.weightPaid) {
        sum += order.isSendOnly && order.weight < 1 ? order.weightRate : order.weight * order.weightRate;
      }

      if (!order.shipThPaid) {
        sum += order.deliveryFeeTh;
      }

      // Extra JP fee lines (e.g. anshin) — flat THB, owed until extraJpFeesPaid
      sum += getUnpaidExtraJpFeesThb(order);
    });
    setTotalSum(sum);
  }

  useEffect(() => {
    if(orders && orders.length > 0)
      calTotalSum(orders)
  },[orders])
  

  // Check if user is admin or superuser
  const isUserAdmin = authState.user && (authState.user.role === 'admin' || authState.user.role === 'superuser');
  // Check if customer is company customer (check from orders)
  const isCompanyCustomer = orders && orders.length > 0 && orders.some(order => order.isCompanyOrder);
  // While links are off, company customers are told to pay the company account
  const bankTransferOnly = useBankTransferOnly();

  // Add debug logging
  console.log('Orders in OrderSummaryTable:', orders);
  console.log('Orders length:', orders?.length);
  console.log('Should render table:', orders && orders.length > 0);

  return (
    <div className={isAdmin ? 'p-4' : ''} id="orderSummaryTable">
      {orders && orders.length > 0 && <table className="min-w-full divide-y divide-gray-200 border border-gray-300 text-center">
        <thead className="bg-blue-50">
          <tr>
            <th scope="col" className={`${style} md:pl-6`}>
              วันที่/สถานะ
            </th>
            <th scope="col" className={`${style}`}>
              รูปสินค้า	
            </th>
            <th scope="col" className={`${style}`}>
              จำนวน
            </th>
            <th scope="col" className={`${style}`}>
              ค่าสินค้า เยน/บาท
            </th>
            <th scope="col" className={`${style}`}>
              ค่าส่งญี่ปุ่น
            </th>
            <th scope="col" className={`${style}`}>
              น้ำหนัก/ค่าน้ำหนัก	
            </th>
            <th scope="col" className={`${style}`}>
              เลขพัสดุ/ค่าส่งไทย
            </th>
            <th scope="col" className={`${style}`}>
              รอบเรือ
            </th>
            <th scope="col" className={`${style}`}>
              ถึงไทย
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {groups && groups.length > 0
            ? groups.map((group) => {
                const groupOrders = orders.filter((o) => o.orderGroup === group.key);
                if (groupOrders.length === 0) return null;
                return (
                  <React.Fragment key={group.key}>
                    <tr>
                      <td colSpan={9} className={groupHeaderStyle}>
                        {group.label}
                        {group.groupPending != null && Number(group.groupPending) !== 0 && (
                          <span className="ml-2 font-semibold text-amber-800">
                            ยอดค้างชำระ: {fmtTotal(group.groupPending)} บาท
                          </span>
                        )}
                      </td>
                    </tr>
                    {groupOrders.map((order) => (
                      <OrderSummaryRow
                        order={order}
                        shop={shopName}
                        key={order.uuid || order.id}
                        isAdmin={isAdmin}
                        onOpenOrderDetail={onOpenOrderDetail}
                      />
                    ))}
                  </React.Fragment>
                );
              })
            : orders && orders.map((order) => (
                <OrderSummaryRow
                  order={order}
                  shop={shopName}
                  key={order.uuid || order.id}
                  isAdmin={isAdmin}
                  onOpenOrderDetail={onOpenOrderDetail}
                />
              ))}
        </tbody>
      </table>}
      
      {/* Show textareas and copy button for admin/superuser with company customers */}
      {orders && orders.length > 0 && isUserAdmin && isCompanyCustomer && orderSummary && (
        <div className='mt-4 space-y-4'>
          <div className='text-left text-xl'>ยอดรวมที่ต้องชำระทั้งหมด : {fmtTotal(totalSum)} บาท</div>
          
          {bankTransferOnly && <div className='p-2 rounded bg-red-100 text-red-700 font-bold text-left'>{COMPANY_BANK_NOTICE}</div>}

          {/* Payment TextAreas Component */}
          <PaymentTextArea
            orders={orders}
            orderSummary={orderSummary}
            smileName={smileName}
            advanceAmount={orderSummary.advanceAmount}
            companyBankOnly={bankTransferOnly}
          />
        </div>
      )}

      {/* Show simple total for non-admin users or non-company customers */}
      {orders && orders.length > 0 && (!isUserAdmin || !isCompanyCustomer) && (
        <>
          {orderSummary && orderSummary.advanceAmount ? (
            <>
              <div className='text-left text-xl mt-5'>มียอดฝากไว้ : {fmtTotal(orderSummary.advanceAmount)} บาท</div>
              <div className='text-left text-xl'>ยอดรวมที่ต้องชำระทั้งหมด : {fmtTotal(totalSum)} - {fmtTotal(orderSummary.advanceAmount)} = {fmtTotal(totalSum - orderSummary.advanceAmount)} บาท</div>
            </>
          ) : (
            <div className='text-left text-xl'>ยอดรวมที่ต้องชำระทั้งหมด : {fmtTotal(totalSum)} บาท</div>
          )}
        </>
      )}
    </div>
  )
}

export default OrderSummaryTable